<template>
	<v-data-table
		:footer-props="{
			itemsPerPageOptions: [10, 15, 20, 25, 30, 50, 100],
			showFirstLastPage: true,
			showCurrentPage: true,
			pagination: {
				page,
				itemsPerPage: rowsPerPage,
				pageStart,
				pageStop,
				pageCount,
				itemsLength: totalCount,
			},
		}"
		:options.sync="options"
		:server-items-length="totalCount"
		hide-default-header
		:hide-default-footer="hideFooter"
		fixed-header
		dense
	>
		<!-- the loader sits outside the scrolling table: inside the header it spans the whole
			table width, so with a horizontal scroll it mostly animates off screen -->
		<template #top>
			<div class="grid-view-progress-anchor">
				<v-progress-linear
					v-if="loadingData"
					indeterminate
					class="grid-view-progress"
					:style="{ top: headerHeight + 'px' }"
				></v-progress-linear>
			</div>
		</template>
		<template #header>
			<thead class="v-data-table-header" ref="header">
				<tr>
					<th v-if="multipleSelection" style="width: 10px">
						<v-checkbox
							v-model="selectAll"
							@click="selectAllMethod"
						></v-checkbox>
					</th>
					<th
						v-for="header in visibleColumns"
						:key="header.FieldName"
						@click="sortItems(header.Id)"
						class="active"
					>
						<span v-if="!getSortIndex(header.Id)">{{
							header.Label
						}}</span>
						<span v-else style="color: black">{{
							header.Label
						}}</span>
						<v-icon
							v-if="showAsc(header.Id)"
							dense
							color="black"
							style="font-size: 18px"
							>mdi-arrow-down</v-icon
						>
						<v-icon
							v-if="showDesc(header.Id)"
							dense
							color="black"
							style="font-size: 18px"
							>mdi-arrow-up</v-icon
						>
						<v-badge
							v-if="getSortIndex(header.Id)"
							:content="getSortIndex(header.Id)"
							inline
							color="#e0e0e0"
						></v-badge>
					</th>
					<th v-if="hasActions" class="fixed-col"></th>
				</tr>
			</thead>
		</template>

		<template #body>
			<tbody v-if="!loadingData">
				<tr
					class="v-data-table__empty-wrapper"
					v-if="gridData && gridData.length == 0"
				>
					<td :colspan="visibleColumns.length + 1">
						No matching records found
					</td>
				</tr>
				<tr
					v-for="item in gridData"
					:key="item.Id"
					@click="rowClick(item, multipleSelection)"
				>
					<td v-if="multipleSelection">
						<v-checkbox
							class="ma-0"
							hide-details
							:input-value="isRowSelected(item)"
							@change="onRowSelected(item)"
						></v-checkbox>
					</td>
					<td
						v-for="column in visibleColumns"
						:key="column.FieldName"
					>
						<template
							v-if="
								column.DataField &&
								column.DataField.DataType ===
									FieldDataTypes.APPROVAL_STATUS
							"
						>
							<span
								v-if="
									item[column.FieldName] ===
									ApprovalStatus.None
								"
								>None</span
							>
							<span
								v-if="
									item[column.FieldName] ===
									ApprovalStatus.Pending
								"
								>Pending</span
							>
							<span
								v-if="
									item[column.FieldName] ===
									ApprovalStatus.Approved
								"
								>Approved</span
							>
						</template>
						<template
							v-if="
								column.DataField &&
								column.DataField.DataType ===
									FieldDataTypes.FILE &&
								item[column.FieldName]
							"
						>
							<v-menu
								order="2"
								content-class="grid-toolbar-menu"
								offset-y
							>
								<template #activator="{ attrs, on }">
									<v-btn
										fab
										style="background-color: transparent"
										x-small
										elevation="0"
										v-bind="attrs"
										v-on="on"
									>
										<v-icon color="primary"
											>mdi-file-download-outline</v-icon
										>
									</v-btn>
								</template>
								<v-list>
									<v-list-item
										single
										@click="
											previewFile(
												item[column.FieldName + '_Path']
											)
										"
									>
										<v-list-item-title
											>Open in New Tab</v-list-item-title
										>
									</v-list-item>
									<v-list-item
										single
										@click="
											downloadFile(item[column.FieldName])
										"
									>
										<v-list-item-title
											>Download</v-list-item-title
										>
									</v-list-item>
								</v-list>
							</v-menu>

							<span
								style="cursor: pointer"
								@click="downloadFile(item[column.FieldName])"
							>
								{{ item[column.FieldName + '_FileName'] }}
							</span>
						</template>
						<template
							v-if="
								column.DataField &&
								(column.DataField.DataType ==
									FieldDataTypes.DATETIME ||
									column.DataField.DataType ==
										FieldDataTypes.AUTOGENERATED_DATETIME)
							"
						>
							{{ getDateTimeFormat(item[column.FieldName]) }}
						</template>
						<template
							v-if="
								column.DataField &&
								column.DataField.DataType == FieldDataTypes.DATE
							"
						>
							{{ getDateFormat(item[column.FieldName]) }}
						</template>
						<template
							v-if="
								column.DataField &&
								column.DataField.DataType == FieldDataTypes.USER
							"
						>
							{{ getUserDisplay(item[column.FieldName]) }}
						</template>
						<template
							v-if="
								column.DataField &&
								column.DataField.DataType ===
									FieldDataTypes.AMOUNT
							"
						>
							{{ getFormatAmount(item[column.FieldName]) }}
						</template>
						<template
							v-if="
								column.DataField &&
								column.DataField.DataType !=
									FieldDataTypes.FILE &&
								column.DataField.DataType !=
									FieldDataTypes.APPROVAL_STATUS &&
								column.DataField.DataType !=
									FieldDataTypes.DATETIME &&
								column.DataField.DataType !=
									FieldDataTypes.DATE &&
								column.DataField.DataType !=
									FieldDataTypes.USER &&
								column.DataField.DataType !=
									FieldDataTypes.AUTOGENERATED_DATETIME &&
								column.DataField.DataType !=
									FieldDataTypes.AMOUNT
							"
						>
							{{ item[column.FieldName] }}
						</template>
					</td>
					<slot name="actionsColumns" :item="item"></slot>
				</tr>
			</tbody>
		</template>
		<template v-slot:footer.prepend>
			<v-btn
				@click="bulkRestore()"
				v-if="deletedGrid && selectedRows.length > 0"
				class="primary mr-4"
			>
				<v-icon>mdi-restore</v-icon>
			</v-btn>
			<span v-if="selectedRows && selectedRows.length > 0"
				>{{ selectedRows.length }} selected rows</span
			>
		</template>
	</v-data-table>
</template>
<script>
import { FieldDataTypes } from '../../../enums/fieldDataTypes';
import { approvalStatus } from '../../../enums/approvalStatus';
import { GeneralSettingsGetters } from '../../../store/getters';
import dataService from '../../../services/data/projectGridsService';
import { mapGetters } from 'vuex';
import projectSettingsMixin from '../../../mixins/projectSettingsMixin';
import { GridsActions } from '../../../store/actions';
import vueAppSubfolder from '../../../utils/appSubfolderUtils';

export default {
	name: 'GridView',
	props: {
		loadingData: Boolean,
		visibleColumns: Array,
		sorting: Array,
		gridData: Array,
		page: Number,
		rowsPerPage: Number,
		totalCount: Number,
		hasActions: Boolean,
		irisUserMetadata: Array,
		multipleSelection: Boolean,
		selectedRows: Array,
		deletedGrid: Boolean,
	},
	data() {
		return {
			//selectedRows: [],
			selectAll: false,
			//dense header height, measured when loading starts so the loader lines up under it
			headerHeight: 32,
		};
	},
	mixins: [projectSettingsMixin],
	watch: {
		visibleColumns(columns) {
			columns.forEach((x) => {
				if (x.DataField.DataType == FieldDataTypes.USER)
					dataService.loadUsers().then((resp) => {
						this.users = resp.data;
					});
			});
		},
		gridData() {
			this.selectAll = this.checkAllRowSelected();
		},
		selectedRows() {
			this.selectAll = this.checkAllRowSelected();
		},
		loadingData: {
			immediate: true,
			handler(value) {
				if (!value) return;
				this.$nextTick(() => {
					if (this.$refs.header)
						this.headerHeight = this.$refs.header.offsetHeight;
				});
			},
		},
	},
	computed: {
		...mapGetters({
			userDisplayFormat: [GeneralSettingsGetters.USER_DISPLAY_FORMAT],
		}),
		sortIndex() {
			let result = [];
			if (!this.sorting) return [];
			let localSorting = JSON.parse(JSON.stringify(this.sorting));
			localSorting.forEach((x, index) =>
				result.push({ FieldId: x.FieldId, Index: index + 1 })
			);
			return result;
		},
		checkForUserField() {
			let result = false;
			this.visibleColumns.forEach((x) => {
				if (x.DataField.DataType == FieldDataTypes.USER) result = true;
			});
			return result;
		},
		hideFooter() {
			if (this.loadingData) return true;
			if (this.gridData && this.gridData.length <= 0) return true;
			return false;
		},
		FieldDataTypes() {
			return FieldDataTypes;
		},
		ApprovalStatus() {
			return approvalStatus;
		},
		pageStart() {
			return this.rowsPerPage * (this.page - 1);
		},
		pageStop() {
			return this.rowsPerPage * this.page;
		},
		pageCount() {
			if (!this.rowsPerPage) return 0;
			if (!this.totalCount) return 0;
			let quotient = Math.floor(this.totalCount / this.rowsPerPage);
			let reminder = this.totalCount % this.rowsPerPage;
			if (reminder === 0) return quotient;
			return quotient + 1;
		},

		options: {
			get() {
				return {
					page: this.page,
					itemsPerPage: this.rowsPerPage,
				};
			},
			set(data) {
				if (this.page != data.page && !isNaN(data.page))
					this.$emit('pageChanged', data.page);
				if (
					this.rowsPerPage != data.itemsPerPage &&
					!isNaN(data.itemsPerPage)
				)
					this.$emit('rowsPerPageChanged', data.itemsPerPage);
			},
		},

		selectedPage() {
			return this.page;
		},
		selectedRowsPerPage() {
			return this.rowsPerPage;
		},
	},

	methods: {
		rowClick(value, multipleSelection) {
			if (!multipleSelection) this.$emit('rowClick', value);
		},
		bulkRestore() {
			this.$emit('bulkRestore');
		},
		previewFile(item) {
			const indexOfFirst = item.indexOf('Resources');
			let filePath = item.substring(indexOfFirst);
			filePath = filePath.replace(`\\`, '/');

			let urlPath =
				window.location.origin +
				(vueAppSubfolder.get() === '/' ? '' : vueAppSubfolder.get()) +
				'/uploads';
			+filePath;
			window.open(urlPath, '_blank');
		},
		downloadFile(item) {
			this.$store.dispatch(GridsActions.DOWNLOAD_FILE, item);
		},

		isRowSelected(row) {
			if (!this.selectedRows) return false;
			if (!row) return false;
			let item = this.selectedRows.find((x) => x == row.Id);
			if (item) return true;
			return false;
		},
		selectAllMethod() {
			this.$emit(
				'rowSelectionChanged',
				this.gridData.map((x) => x.Id),
				this.selectAll
			);
		},

		onRowSelected(row) {
			this.$emit('rowSelectionChanged', [row.Id], null);
		},
		checkAllRowSelected() {
			if (!this.selectedRows) return false;
			let result = true;
			this.gridData.forEach((item) => {
				let rowId = this.selectedRows.find((x) => x == item.Id);
				if (!rowId) result = false;
			});
			return result;
		},
		showDesc(headerId) {
			if (!this.sorting) return false;
			return this.sorting.find(
				(column) => column.FieldId === headerId && column.Operator === 1
			);
		},
		showAsc(headerId) {
			if (!this.sorting) return false;
			return this.sorting.find(
				(column) =>
					column.FieldId === headerId && column.Operator === -1
			);
		},
		sortItems(headerId) {
			let localSorting = JSON.parse(JSON.stringify(this.sorting));
			let sorterIndex = this.sorting.findIndex(
				(column) => column.FieldId === headerId
			);
			if (
				sorterIndex !== -1 &&
				this.sorting[sorterIndex].Operator === -1
			) {
				localSorting.splice(sorterIndex, 1);
			} else if (
				sorterIndex !== -1 &&
				this.sorting[sorterIndex].Operator === 1
			) {
				localSorting[sorterIndex].Operator = -1;
			} else {
				localSorting.push({ Operator: 1, FieldId: headerId });
			}
			this.$emit('sortChanged', localSorting);
		},
		getSortIndex(fieldId) {
			let result = null;
			this.sortIndex.forEach((x) => {
				if (x.FieldId == fieldId) result = x.Index;
			});
			return result;
		},
		getUserDisplay(userId) {
			var user = this.irisUserMetadata.find((x) => x.Id == userId);
			if (user) {
				return user.IrisUserFormat;
			}
			return userId;
		},
	},
};
</script>
<style>
.v-badge__badge {
	font-size: 10px;
	color: black;
	height: 18px;
	min-width: 18px;
}
.v-data-table ::-webkit-scrollbar {
	width: 16px;
	height: 16px;
}
.grid-view-progress-anchor {
	position: relative;
	height: 0;
}
.grid-view-progress.v-progress-linear {
	position: absolute;
	left: 0;
	right: 0;
	/* above the sticky header cells */
	z-index: 3;
}
</style>
